import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import moment from "moment";
import {
  RpaOrganization,
  RpaService,
  RpaVersion,
} from "../../api/services/rpaService";
import { DataState } from "../store/store";

// Helper function to get current timestamp in IST timezone
const getCurrentTimestamp = (): string => {
  return moment()
    .utcOffset("+05:30")
    .format("MMMM DD, YYYY [at] h:mm:ss A [UTC+5:30]");
};

interface RpaConfigurationState {
  organizations: DataState<RpaOrganization[]>;
  versions: DataState<RpaVersion[]>;
  selectedOrganization: RpaOrganization | null;
  selectedVersion: RpaVersion | null;
}

const initialState: RpaConfigurationState = {
  organizations: {
    loading: false,
    error: null,
    data: null,
  },
  versions: {
    loading: false,
    error: null,
    data: null,
  },
  selectedOrganization: null,
  selectedVersion: null,
};

// Fetch all organizations
export const fetchRpaOrganizations = createAsyncThunk(
  "rpaConfiguration/fetchOrganizations",
  async (_, { rejectWithValue }) => {
    try {
      const organizations = await RpaService.getAllOrganizations();
      return organizations;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : "Failed to fetch RPA organizations",
      );
    }
  },
);

// Fetch versions for a specific organization
export const fetchRpaVersions = createAsyncThunk(
  "rpaConfiguration/fetchVersions",
  async (
    { organizationId }: { organizationId: string },
    { rejectWithValue },
  ) => {
    try {
      const versions =
        await RpaService.getVersionsForOrganization(organizationId);
      return versions;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to fetch RPA versions",
      );
    }
  },
);

// Fetch a specific organization
export const fetchRpaOrganization = createAsyncThunk(
  "rpaConfiguration/fetchOrganization",
  async (
    { organizationId }: { organizationId: string },
    { rejectWithValue },
  ) => {
    try {
      const organization = await RpaService.getOrganization(organizationId);
      return organization;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : "Failed to fetch RPA organization",
      );
    }
  },
);

// Fetch a specific version
export const fetchRpaVersion = createAsyncThunk(
  "rpaConfiguration/fetchVersion",
  async (
    {
      organizationId,
      versionId,
    }: { organizationId: string; versionId: string },
    { rejectWithValue },
  ) => {
    try {
      const version = await RpaService.getVersion(organizationId, versionId);
      return version;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to fetch RPA version",
      );
    }
  },
);

// Create a new organization
export const createRpaOrganization = createAsyncThunk(
  "rpaConfiguration/createOrganization",
  async (
    {
      organizationId,
      data,
    }: { organizationId: string; data: Omit<RpaOrganization, "id"> },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const newOrganization = await RpaService.createOrganization(
        organizationId,
        data,
      );
      // Refresh the organizations list after creating
      dispatch(fetchRpaOrganizations());
      return newOrganization;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : "Failed to create RPA organization",
      );
    }
  },
);

// Create a new version with optional data copying
export const createRpaVersionWithCopy = createAsyncThunk(
  "rpaConfiguration/createVersionWithCopy",
  async (
    {
      organizationId,
      versionId,
      sourceVersionId,
    }: {
      organizationId: string;
      versionId: string;
      sourceVersionId?: string;
    },
    { rejectWithValue, dispatch, getState },
  ) => {
    try {
      // Get current user email from auth state
      const state = getState() as any;
      const userEmail = state.firebaseAuthentication?.user?.email || "unknown";

      let initialData: Omit<RpaVersion, "id"> = {
        name: versionId,
        version: versionId,
        created_at: getCurrentTimestamp(),
        updated_at: getCurrentTimestamp(),
        created_by: userEmail,
      };

      // If source version is specified, copy its data
      if (sourceVersionId) {
        const existingVersions = state.rpaConfiguration.versions.data;

        if (existingVersions) {
          const sourceVersion = existingVersions.find(
            (v: RpaVersion) => v.id === sourceVersionId,
          );
          if (sourceVersion) {
            // Copy all data except id, and update metadata
            const { id, created_at, created_by, ...sourceData } = sourceVersion;
            initialData = {
              ...sourceData,
              name: versionId,
              version: versionId,
              created_at: getCurrentTimestamp(),
              updated_at: getCurrentTimestamp(),
              created_by: userEmail,
            };
          }
        }
      }

      // Create the new version
      const newVersion = await RpaService.createVersion(
        organizationId,
        versionId,
        initialData,
      );

      // Refresh the versions list after creating
      dispatch(fetchRpaVersions({ organizationId }));

      return newVersion;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to create RPA version",
      );
    }
  },
);

// Update an existing organization
export const updateRpaOrganization = createAsyncThunk(
  "rpaConfiguration/updateOrganization",
  async (
    {
      organizationId,
      data,
    }: { organizationId: string; data: Partial<RpaOrganization> },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const updatedOrganization = await RpaService.updateOrganization(
        organizationId,
        data,
      );
      // Refresh the organizations list after updating
      dispatch(fetchRpaOrganizations());
      return updatedOrganization;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : "Failed to update RPA organization",
      );
    }
  },
);

// Update an existing version
export const updateRpaVersion = createAsyncThunk(
  "rpaConfiguration/updateVersion",
  async (
    {
      organizationId,
      versionId,
      data,
    }: {
      organizationId: string;
      versionId: string;
      data: Partial<RpaVersion>;
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const updatedVersion = await RpaService.updateVersion(
        organizationId,
        versionId,
        data,
      );
      // Refresh the versions list after updating
      dispatch(fetchRpaVersions({ organizationId }));
      return updatedVersion;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to update RPA version",
      );
    }
  },
);

// Delete an organization
export const deleteRpaOrganization = createAsyncThunk(
  "rpaConfiguration/deleteOrganization",
  async (organizationId: string, { rejectWithValue, dispatch }) => {
    try {
      await RpaService.deleteOrganization(organizationId);
      // Refresh the organizations list after deleting
      dispatch(fetchRpaOrganizations());
      return organizationId;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : "Failed to delete RPA organization",
      );
    }
  },
);

// Delete a version
export const deleteRpaVersion = createAsyncThunk(
  "rpaConfiguration/deleteVersion",
  async (
    {
      organizationId,
      versionId,
    }: { organizationId: string; versionId: string },
    { rejectWithValue, dispatch },
  ) => {
    try {
      await RpaService.deleteVersion(organizationId, versionId);
      // Refresh the versions list after deleting
      dispatch(fetchRpaVersions({ organizationId }));
      return { organizationId, versionId };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to delete RPA version",
      );
    }
  },
);

// Swap process steps in configuration
export const swapProcessSteps = createAsyncThunk(
  "rpaConfiguration/swapProcessSteps",
  async (
    {
      organizationId,
      versionId,
      step1Index,
      step2Index,
    }: {
      organizationId: string;
      versionId: string;
      step1Index: number;
      step2Index: number;
    },
    { rejectWithValue, dispatch, getState },
  ) => {
    try {
      // Get the current state to access the selected version
      const state = getState() as any;
      const selectedVersion = state.rpaConfiguration.selectedVersion;

      if (!selectedVersion || !selectedVersion.configuration) {
        throw new Error("No configuration found to swap");
      }

      // Validate indices
      if (
        step1Index < 0 ||
        step2Index < 0 ||
        step1Index >= selectedVersion.configuration.length ||
        step2Index >= selectedVersion.configuration.length
      ) {
        throw new Error("Invalid step indices for swap operation");
      }

      // Create a new configuration array with swapped steps
      const newConfiguration = [...selectedVersion.configuration];
      const temp = newConfiguration[step1Index];
      newConfiguration[step1Index] = newConfiguration[step2Index];
      newConfiguration[step2Index] = temp;

      // Get current user email from auth state
      const userEmail = state.firebaseAuthentication?.user?.email || "unknown";

      // Prepare only the data that needs to be updated
      const updateData = {
        configuration: newConfiguration,
        updated_at: getCurrentTimestamp(),
        updated_by: userEmail,
      };

      console.log("Swapping steps:", { step1Index, step2Index, updateData });

      // Save to Firebase
      await RpaService.updateVersion(organizationId, versionId, updateData);

      // Return the updated version with all existing data plus the new configuration
      const updatedVersion = {
        ...selectedVersion,
        ...updateData,
      };

      console.log("Swap completed successfully");

      return updatedVersion;
    } catch (error) {
      console.error("Error in swapProcessSteps:", error);
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to swap process steps",
      );
    }
  },
);

// Delete a process step from configuration
export const deleteProcessStep = createAsyncThunk(
  "rpaConfiguration/deleteProcessStep",
  async (
    {
      organizationId,
      versionId,
      stepIndex,
    }: {
      organizationId: string;
      versionId: string;
      stepIndex: number;
    },
    { rejectWithValue, dispatch, getState },
  ) => {
    try {
      // Get the current state to access the selected version
      const state = getState() as any;
      const selectedVersion = state.rpaConfiguration.selectedVersion;

      if (!selectedVersion || !selectedVersion.configuration) {
        throw new Error("No configuration found to delete from");
      }

      // Validate index
      if (stepIndex < 0 || stepIndex >= selectedVersion.configuration.length) {
        throw new Error("Invalid step index for delete operation");
      }

      console.log("=== REDUX DELETE OPERATION START ===");
      console.log("Original configuration:", selectedVersion.configuration);
      console.log("Step index to delete:", stepIndex);
      console.log(
        "Step being deleted:",
        selectedVersion.configuration[stepIndex],
      );

      // Create a new configuration array without the deleted step
      const newConfiguration = selectedVersion.configuration.filter(
        (_, index) => index !== stepIndex,
      );

      console.log("New configuration after filter:", newConfiguration);
      console.log(
        "Length change:",
        selectedVersion.configuration.length,
        "->",
        newConfiguration.length,
      );

      // Get current user email from auth state
      const userEmail = state.firebaseAuthentication?.user?.email || "unknown";

      // Prepare the data that needs to be updated
      const updateData = {
        configuration: newConfiguration,
        updated_at: getCurrentTimestamp(),
        updated_by: userEmail,
      };

      console.log("Update data prepared:", updateData);

      // Save to Firebase
      await RpaService.updateVersion(organizationId, versionId, updateData);

      // Return the updated version with all existing data plus the new configuration
      const updatedVersion = {
        ...selectedVersion,
        ...updateData,
      };

      console.log("Delete completed successfully");

      return updatedVersion;
    } catch (error) {
      console.error("Error in deleteProcessStep:", error);
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : "Failed to delete process step",
      );
    }
  },
);

// Add a new process step to configuration
export const addProcessStep = createAsyncThunk(
  "rpaConfiguration/addProcessStep",
  async (
    {
      organizationId,
      versionId,
      stepData,
      insertIndex,
    }: {
      organizationId: string;
      versionId: string;
      stepData: any;
      insertIndex: number;
    },
    { rejectWithValue, dispatch, getState },
  ) => {
    try {
      // Get the current state to access the selected version
      const state = getState() as any;
      const selectedVersion = state.rpaConfiguration.selectedVersion;

      if (!selectedVersion) {
        throw new Error("No version selected to add step to");
      }

      // Initialize configuration array if it doesn't exist
      const currentConfiguration = selectedVersion.configuration || [];

      // Validate insert index
      if (insertIndex < 0 || insertIndex > currentConfiguration.length) {
        throw new Error("Invalid insert index for add operation");
      }

      console.log("=== REDUX ADD STEP OPERATION START ===");
      console.log("Original configuration:", currentConfiguration);
      console.log("Step data to add:", stepData);
      console.log("Insert index:", insertIndex);

      // Create a new configuration array with the new step inserted
      const newConfiguration = [...currentConfiguration];
      newConfiguration.splice(insertIndex, 0, stepData);

      console.log("New configuration after insert:", newConfiguration);
      console.log(
        "Length change:",
        currentConfiguration.length,
        "->",
        newConfiguration.length,
      );

      // Get current user email from auth state
      const userEmail = state.firebaseAuthentication?.user?.email || "unknown";

      // Prepare the data that needs to be updated
      const updateData = {
        configuration: newConfiguration,
        updated_at: getCurrentTimestamp(),
        updated_by: userEmail,
      };

      console.log("Update data prepared:", updateData);

      // Save to Firebase
      await RpaService.updateVersion(organizationId, versionId, updateData);

      // Return the updated version with all existing data plus the new configuration
      const updatedVersion = {
        ...selectedVersion,
        ...updateData,
      };

      console.log("Add step completed successfully");

      return updatedVersion;
    } catch (error) {
      console.error("Error in addProcessStep:", error);
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to add process step",
      );
    }
  },
);

const rpaConfigurationSlice = createSlice({
  name: "rpaConfiguration",
  initialState,
  reducers: {
    clearRpaData: (state) => {
      state.organizations.data = null;
      state.organizations.error = null;
      state.versions.data = null;
      state.versions.error = null;
      state.selectedOrganization = null;
      state.selectedVersion = null;
    },
    setSelectedOrganization: (
      state,
      action: PayloadAction<RpaOrganization | null>,
    ) => {
      state.selectedOrganization = action.payload;
      // Clear versions when organization changes
      state.versions.data = null;
      state.selectedVersion = null;
    },
    setSelectedVersion: (state, action: PayloadAction<RpaVersion | null>) => {
      state.selectedVersion = action.payload;
    },
    // Local swap action for immediate UI update
    swapStepsLocally: (
      state,
      action: PayloadAction<{ step1Index: number; step2Index: number }>,
    ) => {
      if (
        state.selectedVersion &&
        state.selectedVersion.configuration &&
        Array.isArray(state.selectedVersion.configuration)
      ) {
        const { step1Index, step2Index } = action.payload;
        const config = [...state.selectedVersion.configuration];
        const temp = config[step1Index];
        config[step1Index] = config[step2Index];
        config[step2Index] = temp;
        state.selectedVersion.configuration = config;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch organizations
      .addCase(fetchRpaOrganizations.pending, (state) => {
        state.organizations.loading = true;
        state.organizations.error = null;
      })
      .addCase(
        fetchRpaOrganizations.fulfilled,
        (state, action: PayloadAction<RpaOrganization[]>) => {
          state.organizations.loading = false;
          state.organizations.data = action.payload;
        },
      )
      .addCase(fetchRpaOrganizations.rejected, (state, action) => {
        state.organizations.loading = false;
        state.organizations.error = action.payload as string;
      })

      // Fetch versions
      .addCase(fetchRpaVersions.pending, (state) => {
        state.versions.loading = true;
        state.versions.error = null;
      })
      .addCase(
        fetchRpaVersions.fulfilled,
        (state, action: PayloadAction<RpaVersion[]>) => {
          state.versions.loading = false;
          state.versions.data = action.payload;
        },
      )
      .addCase(fetchRpaVersions.rejected, (state, action) => {
        state.versions.loading = false;
        state.versions.error = action.payload as string;
      })

      // Fetch specific organization
      .addCase(fetchRpaOrganization.pending, (state) => {
        state.organizations.loading = true;
        state.organizations.error = null;
      })
      .addCase(
        fetchRpaOrganization.fulfilled,
        (state, action: PayloadAction<RpaOrganization>) => {
          state.organizations.loading = false;
          state.selectedOrganization = action.payload;
        },
      )
      .addCase(fetchRpaOrganization.rejected, (state, action) => {
        state.organizations.loading = false;
        state.organizations.error = action.payload as string;
      })

      // Fetch specific version
      .addCase(fetchRpaVersion.pending, (state) => {
        state.versions.loading = true;
        state.versions.error = null;
      })
      .addCase(
        fetchRpaVersion.fulfilled,
        (state, action: PayloadAction<RpaVersion>) => {
          state.versions.loading = false;
          state.selectedVersion = action.payload;
        },
      )
      .addCase(fetchRpaVersion.rejected, (state, action) => {
        state.versions.loading = false;
        state.versions.error = action.payload as string;
      })

      // Create organization
      .addCase(createRpaOrganization.pending, (state) => {
        state.organizations.loading = true;
        state.organizations.error = null;
      })
      .addCase(createRpaOrganization.fulfilled, (state) => {
        state.organizations.loading = false;
        // We refresh the full list via the side effect in the thunk
      })
      .addCase(createRpaOrganization.rejected, (state, action) => {
        state.organizations.loading = false;
        state.organizations.error = action.payload as string;
      })

      // Create version
      .addCase(createRpaVersionWithCopy.pending, (state) => {
        state.versions.loading = true;
        state.versions.error = null;
      })
      .addCase(createRpaVersionWithCopy.fulfilled, (state) => {
        state.versions.loading = false;
        // We refresh the full list via the side effect in the thunk
      })
      .addCase(createRpaVersionWithCopy.rejected, (state, action) => {
        state.versions.loading = false;
        state.versions.error = action.payload as string;
      })

      // Update organization
      .addCase(updateRpaOrganization.pending, (state) => {
        state.organizations.loading = true;
        state.organizations.error = null;
      })
      .addCase(updateRpaOrganization.fulfilled, (state) => {
        state.organizations.loading = false;
        // We refresh the full list via the side effect in the thunk
      })
      .addCase(updateRpaOrganization.rejected, (state, action) => {
        state.organizations.loading = false;
        state.organizations.error = action.payload as string;
      })

      // Update version
      .addCase(updateRpaVersion.pending, (state) => {
        state.versions.loading = true;
        state.versions.error = null;
      })
      .addCase(
        updateRpaVersion.fulfilled,
        (state, action: PayloadAction<RpaVersion>) => {
          state.versions.loading = false;
          // Update the selected version with the new data if it matches
          if (
            state.selectedVersion &&
            state.selectedVersion.id === action.payload.id
          ) {
            state.selectedVersion = action.payload;
          }
          // Also update it in the versions list if it exists
          if (state.versions.data) {
            const versionIndex = state.versions.data.findIndex(
              (v) => v.id === action.payload.id,
            );
            if (versionIndex !== -1) {
              state.versions.data[versionIndex] = action.payload;
            }
          }
        },
      )
      .addCase(updateRpaVersion.rejected, (state, action) => {
        state.versions.loading = false;
        state.versions.error = action.payload as string;
      })

      // Delete organization
      .addCase(deleteRpaOrganization.pending, (state) => {
        state.organizations.loading = true;
        state.organizations.error = null;
      })
      .addCase(deleteRpaOrganization.fulfilled, (state) => {
        state.organizations.loading = false;
        // We refresh the full list via the side effect in the thunk
      })
      .addCase(deleteRpaOrganization.rejected, (state, action) => {
        state.organizations.loading = false;
        state.organizations.error = action.payload as string;
      })

      // Delete version
      .addCase(deleteRpaVersion.pending, (state) => {
        state.versions.loading = true;
        state.versions.error = null;
      })
      .addCase(deleteRpaVersion.fulfilled, (state) => {
        state.versions.loading = false;
        // We refresh the full list via the side effect in the thunk
      })
      .addCase(deleteRpaVersion.rejected, (state, action) => {
        state.versions.loading = false;
        state.versions.error = action.payload as string;
      })

      // Swap process steps
      .addCase(swapProcessSteps.pending, (state) => {
        state.versions.loading = true;
        state.versions.error = null;
      })
      .addCase(
        swapProcessSteps.fulfilled,
        (state, action: PayloadAction<RpaVersion>) => {
          state.versions.loading = false;
          // Update the selected version with the new configuration
          state.selectedVersion = action.payload;
          // Also update it in the versions list if it exists
          if (state.versions.data) {
            const versionIndex = state.versions.data.findIndex(
              (v) => v.id === action.payload.id,
            );
            if (versionIndex !== -1) {
              state.versions.data[versionIndex] = action.payload;
            }
          }
        },
      )
      .addCase(swapProcessSteps.rejected, (state, action) => {
        state.versions.loading = false;
        state.versions.error = action.payload as string;
      })

      // Delete process step
      .addCase(deleteProcessStep.pending, (state) => {
        state.versions.loading = true;
        state.versions.error = null;
      })
      .addCase(
        deleteProcessStep.fulfilled,
        (state, action: PayloadAction<RpaVersion>) => {
          state.versions.loading = false;
          // Update the selected version with the new configuration
          state.selectedVersion = action.payload;
          // Also update it in the versions list if it exists
          if (state.versions.data) {
            const versionIndex = state.versions.data.findIndex(
              (v) => v.id === action.payload.id,
            );
            if (versionIndex !== -1) {
              state.versions.data[versionIndex] = action.payload;
            }
          }
        },
      )
      .addCase(deleteProcessStep.rejected, (state, action) => {
        state.versions.loading = false;
        state.versions.error = action.payload as string;
      })

      // Add process step
      .addCase(addProcessStep.pending, (state) => {
        state.versions.loading = true;
        state.versions.error = null;
      })
      .addCase(
        addProcessStep.fulfilled,
        (state, action: PayloadAction<RpaVersion>) => {
          state.versions.loading = false;
          // Update the selected version with the new configuration
          state.selectedVersion = action.payload;
          // Also update it in the versions list if it exists
          if (state.versions.data) {
            const versionIndex = state.versions.data.findIndex(
              (v) => v.id === action.payload.id,
            );
            if (versionIndex !== -1) {
              state.versions.data[versionIndex] = action.payload;
            }
          }
        },
      )
      .addCase(addProcessStep.rejected, (state, action) => {
        state.versions.loading = false;
        state.versions.error = action.payload as string;
      });
  },
});

export const {
  clearRpaData,
  setSelectedOrganization,
  setSelectedVersion,
  swapStepsLocally,
} = rpaConfigurationSlice.actions;

export default rpaConfigurationSlice.reducer;
